import { useEffect, useMemo, useState } from "react";
import { Banner, Card, SectionTitle, formatBytes } from "../components/ui";
import type { Api } from "../lib/api";
import type { BuildArtifact } from "../lib/types";

export function buildGroups(builds: BuildArtifact[]): BuildArtifact[][] {
  const rpm = new Map<string, BuildArtifact[]>();
  const singles: BuildArtifact[][] = [];
  for (const build of builds) {
    if (build.pipeline !== "release-rpm-el10") { singles.push([build]); continue; }
    const key = `${build.version.length}:${build.version}${build.buildNumber.length}:${build.buildNumber}`;
    rpm.set(key, [...(rpm.get(key) ?? []), build]);
  }
  return [...singles, ...[...rpm.values()].filter((pair) => pair.length === 2 &&
    pair.some((b) => b.kind === "portal") && pair.some((b) => b.kind === "selinux-policy"))
    .map((pair) => pair.sort((a, b) => a.kind === "portal" ? -1 : b.kind === "portal" ? 1 : 0))];
}

export function newestBuildBanner(groups: BuildArtifact[][]): string | null {
  const group = [...groups].sort((a, b) => b[0]!.publishedAt.localeCompare(a[0]!.publishedAt))[0];
  if (!group) return null;
  const build = group[0]!;
  const date = new Date(build.publishedAt).toLocaleDateString();
  if (build.pipeline === "release-rpm-el10") {
    return `Latest portal + SELinux policy RPM pair available: ${build.version} (${build.pipeline}): ${group.map((member) => member.objectPath.split("/").at(-1)).join(" + ")} published ${date}.`;
  }
  return `Latest build available: ${build.version} (${build.pipeline}) published ${date}.`;
}

/** Builds — build downloads. Only entitled release lines are ever returned. §4.3 */
export function Flow({ api }: { api: Api }) {
  const [builds, setBuilds] = useState<BuildArtifact[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyPath, setBusyPath] = useState<string | null>(null);

  useEffect(() => {
    api
      .listBuilds()
      .then((r) => setBuilds(r.builds))
      .catch(() => setError("Could not load the build catalog."));
  }, [api]);

  const groups = useMemo(() => buildGroups(builds ?? []), [builds]);
  const newest = useMemo(() => newestBuildBanner(groups), [groups]);

  async function download(b: BuildArtifact) {
    setBusyPath(b.objectPath);
    setError(null);
    try {
      const { url } = await api.downloadUrl(b.pipeline, b.objectPath);
      window.location.assign(url);
    } catch {
      setError("Download was denied or the link could not be minted.");
    } finally {
      setBusyPath(null);
    }
  }

  return (
    <div>
      <SectionTitle
        kicker="Builds · Move & Accelerate"
        title="Build downloads"
        desc="Signed PumaMesh release tarballs and complete portal + SELinux policy RPM pairs for your entitled lines. Each file has its own short-lived, audited download link."
      />

      {error && (
        <div className="mb-6">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      {newest && (
        <div className="mb-6">
          <Banner tone="success">
            {newest}
          </Banner>
        </div>
      )}

      {!builds && !error && <p className="text-sm text-ink-muted">Loading catalog…</p>}
      {builds && groups.length === 0 && (
        <Card>
          <p className="text-sm text-ink-muted">No builds are available for your entitlements yet.</p>
        </Card>
      )}

      <div className="grid gap-4">
        {groups.map((group) => {
          const b = group[0]!;
          return <Card key={`${b.pipeline}:${b.objectPath}`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold text-ink">PumaMesh {b.version}</span>
                  <span className="badge-accent">{b.pipeline}</span>
                  <span className="badge">build #{b.buildNumber}</span>
                </div>
                <div className="mt-1 text-sm text-ink-muted">
                  {b.os} · {b.arch} · {formatBytes(b.sizeBytes)} ·{" "}
                  {new Date(b.publishedAt).toLocaleDateString()}
                </div>
                {group.map((file) => <div key={file.objectPath} className="mt-3 space-y-1 font-mono text-xs text-ink-dim">
                  <div>{file.kind === "portal" ? "Portal RPM" : file.kind === "selinux-policy" ? "SELinux policy RPM" : "Tarball"} · {file.arch}</div>
                  <div className="truncate">sha256: {file.sha256}</div>
                  {file.signature.tool && file.signature.tool !== "unknown" && <div>signature: {file.signature.tool}{file.signature.keyId ? ` · key ${file.signature.keyId}` : ""}</div>}
                </div>)}
              </div>
              <div className="flex shrink-0 flex-col items-stretch gap-2 md:items-end">
                {group.map((file) => <button key={file.objectPath}
                  className="btn-primary"
                  disabled={busyPath === file.objectPath}
                  onClick={() => void download(file)}
                >
                  {busyPath === file.objectPath ? "Preparing…" : `Download ${file.kind === "portal" ? "portal RPM" : file.kind === "selinux-policy" ? "SELinux policy RPM" : "tarball"}`}
                </button>)}
                {b.releaseNotesUrl && (
                  <a
                    className="text-xs text-ink-muted hover:text-accent-strong hover:underline"
                    href={b.releaseNotesUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Release notes ↗
                  </a>
                )}
              </div>
            </div>
          </Card>;
        })}
      </div>
    </div>
  );
}
